/* eslint-disable react-hooks/exhaustive-deps */
import {faMessage, faSave} from '@fortawesome/pro-solid-svg-icons';
import {Badge, CircularProgress, Drawer, Fab, Grid, Tab, Tabs} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {DialogType} from 'commons/Dialog/CustomDialog';
import CustomIcon from 'commons/Icon/CustomIcon';
import LocalizedText, {localize} from 'commons/LocalizedText/LocalizedText';
import NotesContainer from 'components/Note/NotesContainer';
import {BasicPageContainer} from 'components/PageContainer/BasicPageContainer';
import PageContent from 'components/PageContent/PageContent';
import {ProductTestContactsPage} from 'components/ProductTest/ProductTestContactsPage';
import {ProductTestDocumentsPage} from 'components/ProductTest/ProductTestDocumentsPage';
import {ProductTestGeneralPage} from 'components/ProductTest/ProductTestGeneralPage';
import {ProductTestMachineInformation} from 'components/ProductTest/ProductTestMachineInformation';
import {ProductTestTestedPointsPage} from 'components/ProductTest/ProductTestTestedPointsPage';
import SkeletonLaboratoryCard from 'components/Skeletons/SkeletonLaboratoryCard/SkeletonLaboratoryCard';
import {useDialog} from 'hook/DialogProvider';
import {useLaboratoryTestInfo} from 'hook/UseLaboratoryTestInfo';
import useLocalizedSnackBar from 'hook/UseLocalizedSnackBar';
import useQueryState from 'hook/UseQueryState';
import React, {Suspense, useCallback, useEffect, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {NoteService} from 'services/NoteService';
import {ProductTestService} from 'services/ProductTestService';
import {ErrorName} from 'utils/constants/error.name';
import {ParamTypes} from 'utils/constants/param.type';
import {GET_PRODUCT_TEST_TABS, PRODUCT_TEST_TABS_OBJECT} from 'utils/constants/tabs';
import {useNoteContext} from "hook/NoteProvider";


const useStyles = makeStyles((theme) => ({
  container: {
    padding: '100px 0'
  },
  tabs: {
    marginBottom: '10px'
  },
  tab: {
    minHeight: 67,
    minWidth: 50,
    padding: '3px 10px',
    fontSize: '112%',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.menuHover
    },
    '& .MuiTab-wrapper > *:first-child': {
      marginBottom: 3
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '95%'
    }
  },
  controlsContainer: {
    position: 'fixed',
    left: '90%',
    bottom: '15%',
    textAlign: 'center',
    width: '5%'
  },
  saveContainer: {
    cursor: 'pointer',
    position: 'absolute',
    height: 90,
    width: 90,
    fontSize: '200%'
  },
  noteContainer: {
    cursor: 'pointer',
    bottom: 20,
    left: '67%',
    position: 'absolute',
    fontSize: '150%',
    backgroundColor: theme.palette.background.paper
  },
  saveIcon: {
    color: theme.palette.background.paper
  },
  noteIcon: {
    color: theme.palette.primary.main
  },
  loadingSave: {
    position: 'absolute',
    left: '62%'
  },
  noteNumber: {
    fontSize: '14px',
    bottom: 17,
    position: 'absolute'
  },
  loadingNote: {
    bottom: 22,
    position: 'absolute',
    color: theme.palette.background.paper
  }
}));

const GENERAL_TAB = 0;
const MACHINE_INFORMATION_TAB = 1;
const TESTED_POINTS_TAB = 2;
const CONTACTS_TAB = 4;

const TABS_WITH_CONTROLS = [
  GENERAL_TAB,
  MACHINE_INFORMATION_TAB,
  CONTACTS_TAB
  /*Not yet implemented, waiting for client approval*/
  /*TESTED_POINTS_TAB*/
];

export const TestPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const { ptldId, laboratory } = useParams();
  const initialPtld = useLaboratoryTestInfo();
  const testTabs = GET_PRODUCT_TEST_TABS();
  const showDialog = useDialog();
  const showSnackbar = useLocalizedSnackBar();
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingNote, setIsLoadingNote] = useState(true);
  const [isLoadingTestDetails, setIsLoadingTestDetails] = useState(true);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);
  const [isLoadingLaboReportGrid, setIsLoadingLaboReportGrid] = useState(false);
  const [isLoadingProviderBill, setisLoadingProviderBill] = useState(false);
  const [testDetails, setTestDetails] = useState(null);
  const [noteNumber, setNoteNumber] = useState(0);
  const [unreadNoteNumber, setUnreadNoteNumber] = useState(0);
  const [notes, setNotes] = useState([]);
  const [displayNote, setDisplayNote] = useState(location.state?.displayNote ?? false);
  const [noteEditionMode, setNoteEditionMode] = useState(false);
  const [generalPageHasChanges, setGeneralPageHasChanges] = useState(false);
  const [machineInfoPageHasChanges, setMachineInfoPageHasChanges] = useState(false);
  const [testedPointsPageHasChanges, setTestedPointsPageHasChanges] = useState(false);
  const handleChangeTab = useCallback((_, tab) => setTabSelected(tab), []);
  const [tabSelected, setTabSelected] = useQueryState(
    'tabSelected',
    0,
    ParamTypes.NUMBER
  );
  const [displayControls, setDisplayControls] = useState(true);
  const [productTestTestedPoints, setProductTestTestedPoints] = useState([]);
  const [isAllOpen, setIsAllOpen] = useState(false);
  const [changedFields, setChangedFields] = useState([]);
  const [, setIsPendingNoteNeedToRefresh] = useNoteContext();

  useEffect(() => {
    if((testDetails?.ptldGeneralProjection?.ptldRecid && tabSelected === TESTED_POINTS_TAB) && productTestTestedPoints.length === 0){
      setIsLoadingApplications(true);
      ProductTestService.getProductTestApplications(testDetails?.ptldGeneralProjection.ptldRecid).then(response => {
        setProductTestTestedPoints(response);
        setIsLoadingApplications(false);
      }).catch(errorMessage => {
        setIsLoadingApplications(false);
        if (errorMessage !== ErrorName.ABORT_ERROR) {
          showDialog({
            variant: DialogType.ERROR,
            title: localize('error'),
            description: errorMessage
          });
        }
      });
    }
  }, [tabSelected]);

  useEffect(() => {
    if(location.state?.isNewTestNumber){
      setIsLoadingNote(true);
      setIsLoadingTestDetails(true);
    }
    setDisplayNote(location.state?.displayNote);
  }, [location]);

  useEffect(() => {
    setDisplayControls(TABS_WITH_CONTROLS.includes(tabSelected));
  }, [tabSelected]);


  const getTestNotes = ptldId => {
    setIsLoadingNote(true);
    setNotes([]);
    ProductTestService.getNotesByPtldId(ptldId).then(resp => {
      setIsLoadingNote(false);
      setNoteNumber(resp.length);
      setNotes(resp);
    }).catch(errorMessage => {
      setIsLoadingNote(false);
      if (errorMessage !== ErrorName.ABORT_ERROR) {
        showDialog({
          variant: DialogType.ERROR,
          title: localize('error'),
          description: errorMessage
        });
      }
    });
  };

  const getUnreadNotesNumber = () => {
    ProductTestService.getUnreadNotesNumberByPtldId(ptldId).then(resp => {
      setUnreadNoteNumber(resp);
    }).catch(errorMessage => {
      setIsLoadingNote(false);
      if (errorMessage !== ErrorName.ABORT_ERROR) {
        showDialog({
          variant: DialogType.ERROR,
          title: localize('error'),
          description: errorMessage
        });
      }
    });
  }

  useEffect(() => {
    if (initialPtld && Object.keys(initialPtld).length > 0 ) {
      setTestDetails(initialPtld);
      setIsLoadingTestDetails(false);
      getTestNotes(initialPtld.ptldGeneralProjection?.ptldRecid);
      getUnreadNotesNumber(initialPtld.ptldGeneralProjection?.ptldRecid);
    }
  }, [initialPtld]);

  const clickDeleteNote = noteRecid => {
    showDialog({
      variant: DialogType.CONFIRMATION,
      title: 'confirmAction',
      description: localize('deleteNoteConfirmation'),
      onSubmit: () => handleDeleteNote(noteRecid)
    });
  };

  const handleDeleteNote = noteRecid => {
    NoteService.deleteNote(noteRecid).then(() => {
      showSnackbar('noteDeleted', {variant: 'success'});
      setNoteEditionMode(false);
      setIsLoadingNote(true);
      getTestNotes(testDetails?.ptldGeneralProjection.ptldRecid);
    }).catch(() => {
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: 'errorWhileDeletingNote'
      });
    })
  };

  const handleSaveNote = newNote => {
    const noteToSave = {
      ...newNote,
      laboratoryRecid: laboratory
    };
    NoteService.addProductTestLaboDetailsNote(testDetails?.ptldGeneralProjection.ptldRecid, noteToSave).then(() => {
      showSnackbar(noteToSave.recid ? 'noteModified': 'noteAdded', { variant: 'success' });
      setNoteEditionMode(false);
      setIsLoadingNote(true);
      getTestNotes(testDetails?.ptldGeneralProjection.ptldRecid);
    }).catch(() => {
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: 'errorWhileAddingNote'
      });
    })
  }

  const handleDelete = (row) => {
    setIsLoadingLaboReportGrid(true);
    ProductTestService.deleteProductTestLaboDetailDocument(ptldId, row.idCrypted)
      .then((response) => {
        setTestDetails(response);
        showSnackbar('fileDeleted', { variant: 'success' });
      })
      .catch((errorMessage) => {
        showDialog({
          variant: DialogType.ERROR,
          title: 'error',
          description: errorMessage
        });
      })
      .finally(() => setIsLoadingLaboReportGrid(false));
  }


  const handleSave = () => {
    setIsLoadingSave(true);
    let dataToSend = {};
    if (generalPageHasChanges) {
      dataToSend = {ptldGeneralProjection: testDetails.ptldGeneralProjection};
      setGeneralPageHasChanges(false);
    }
    if (machineInfoPageHasChanges) {
      dataToSend = {
        ptldMachineInfoProjection: {
          deliveryDate: testDetails.ptldMachineInfoProjection.deliveryDate
        }
      };
      delete dataToSend.ptldMachineInfoProjection.tdsFiles;
      setMachineInfoPageHasChanges(false);
    }
    if (testedPointsPageHasChanges) {
      console.log('Not yet implemented, waiting for client approval');
      setTestedPointsPageHasChanges(false);
    }
    ProductTestService.modifyTestDetails(ptldId, dataToSend).then((response) =>{
      setTestDetails(response);
      showSnackbar('laboratoryInfoModified', { variant: 'success' })
    }).catch(() => showDialog({
      variant: DialogType.ERROR,
      title: 'error',
      description: 'errorWhileModifyingLaboratoryInfo'
    }));
    setIsLoadingSave(false);
    setChangedFields([]);
  }

  const handleUploadProductTestDocument = (file, reportType) => {
    setIsLoadingLaboReportGrid(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("reportType", reportType);
    ProductTestService.uploadProductTestLaboDetailDocument(ptldId, formData)
      .then((response) =>{
        setTestDetails(response);
      })
      .catch((errorMessage) => {
        showDialog({
          variant: DialogType.ERROR,
          title: 'error',
          description: errorMessage
        });
      })
      .finally(() => setIsLoadingLaboReportGrid(false));
  }

  const handleUploadProviderBillDocument = (
    file,
    providerBillRefNumber,
    providerBillEmissionDate,
    linkedOrderFormRecids
  ) => {

    setisLoadingProviderBill(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("providerBillRefNumber", providerBillRefNumber);
    formData.append("providerBillEmissionDate", providerBillEmissionDate.getTime());
    formData.append("linkedOrderFormRecids", linkedOrderFormRecids);
    ProductTestService.uploadProviderBillDocument(ptldId, formData)
      .then((response) =>{
        handleSave();
        setTestDetails(response);
      })
      .catch((errorMessage) => {
        showDialog({
          variant: DialogType.ERROR,
          title: 'error',
          description: errorMessage
        });
      })
      .finally(() => {
        setisLoadingProviderBill(false);
      });
  }


  const handleOnChange = (value, fields) => {
    const productTestDetailsTmp = {...testDetails};
    const changedFieldsTmp = [...changedFields];
    !changedFieldsTmp?.includes(fields.at(-1)) && changedFieldsTmp.push(fields.at(-1));
    setChangedFields(changedFieldsTmp);
    if(fields.length > 1){
      productTestDetailsTmp[fields[0]][fields[1]] = value;
    } else {
      switch (tabSelected) {
      case GENERAL_TAB:
        productTestDetailsTmp.ptldGeneralProjection[fields[0]] = value;
        setGeneralPageHasChanges(true);
        break;
      case MACHINE_INFORMATION_TAB:
        productTestDetailsTmp.ptldMachineInfoProjection[fields[0]] = value;
        setMachineInfoPageHasChanges(true);
        break;
      case TESTED_POINTS_TAB:
        console.log('Not yet implemented, waiting for client approval')
        setTestedPointsPageHasChanges(true);
        break;
      default:
        break;
      }
    }
    setTestDetails(productTestDetailsTmp);
  }

  const handleNoteOpen = () => {
    getTestNotes(testDetails?.ptldGeneralProjection?.ptldRecid);
    setDisplayNote(true);
  }

  const handleDrawerClose = () => {
    setDisplayNote(false);
    setNoteEditionMode(false);
    setIsPendingNoteNeedToRefresh(true);
  }

  const handleOpenTestedPoints = applicationRecid => {
    const pttdsTmp = [...productTestTestedPoints];
    if(applicationRecid){
      const productTestTestedPointIndex = pttdsTmp.findIndex(pttd => pttd.application.recid === applicationRecid);
      if(productTestTestedPointIndex !== -1){
        const pttdTmp = {...pttdsTmp[productTestTestedPointIndex]};
        pttdTmp.isOpen = !pttdTmp.isOpen
        pttdsTmp.splice(productTestTestedPointIndex, 1, pttdTmp);
        setProductTestTestedPoints([...pttdsTmp]);
      }
    }else{
      pttdsTmp.forEach(pttd => {
        pttd.isOpen = isAllOpen
      });
      setProductTestTestedPoints([...pttdsTmp]);
    }
  }

  const addAsteriskToTabName = useCallback(currentTab => {
    if ((currentTab.LABEL === PRODUCT_TEST_TABS_OBJECT.GENERAL.LABEL && generalPageHasChanges)
      || (currentTab.LABEL === PRODUCT_TEST_TABS_OBJECT.TESTED_POINTS.LABEL && testedPointsPageHasChanges)
      || (currentTab.LABEL === PRODUCT_TEST_TABS_OBJECT.MACHINE_INFORMATION.LABEL && machineInfoPageHasChanges)) {
      return '*';
    }
    return '';
  }, [generalPageHasChanges, testedPointsPageHasChanges, machineInfoPageHasChanges]);

  useEffect(() => {
    handleOpenTestedPoints();
  }, [isAllOpen])

  return (
    <>
      <Drawer anchor="right" onClose={handleDrawerClose} open={displayNote}>
        <NotesContainer
          getUnreadNotesNumber={getUnreadNotesNumber}
          handleDeleteNote={clickDeleteNote}
          handleSaveNote={handleSaveNote}
          isLoadingNote={isLoadingNote}
          noteEditionMode={noteEditionMode}
          notes={notes}
          onCloseDrawer={handleDrawerClose}
          setNoteEditionMode={setNoteEditionMode}
        />
      </Drawer>
      <BasicPageContainer>
        <PageContent lowPaddingTop>
          <Tabs
            centered
            className={classes.tabs}
            indicatorColor="secondary"
            onChange={handleChangeTab}
            textColor="secondary"
            value={tabSelected}
            variant="fullWidth"
          >
            {testTabs.map((currentTab) => (
              <Tab
                classes={{
                  root: classes.tab
                }}
                icon={<CustomIcon icon={currentTab.ICON} size="lg" />}
                key={currentTab.LABEL}
                label={<LocalizedText className="bold" suffix={`${addAsteriskToTabName(currentTab)}`} text={`${currentTab.LABEL}`} />}
              />
            ))}
          </Tabs>
          <Suspense fallback={SkeletonLaboratoryCard()}>
            {[
              <ProductTestGeneralPage
                changedFields={changedFields}
                generalPageHasChanges={generalPageHasChanges}
                isLoadingTestDetails={isLoadingTestDetails}
                onChange={handleOnChange}
                productTestGeneral={testDetails?.ptldGeneralProjection}
              />,
              <ProductTestMachineInformation
                changedFields={changedFields}
                isLoadingTestDetails={isLoadingTestDetails}
                machineInfoPageHasChanges={machineInfoPageHasChanges}
                onChange={handleOnChange}
                productTestMachineInfo={testDetails?.ptldMachineInfoProjection}
              />,
              <ProductTestTestedPointsPage
                changedFields={changedFields}
                handleOpenTestedPoints={handleOpenTestedPoints}
                isAllOpen={isAllOpen}
                isLoadingApplications={isLoadingApplications}
                onChange={handleOnChange}
                productTestTestedPoints={productTestTestedPoints}
                setIsAllOpen={setIsAllOpen}
                testedPointsPageHasChanges={testedPointsPageHasChanges}
              />,
              <ProductTestDocumentsPage
                availableActions={testDetails?.availableActions}
                handleDelete={handleDelete}
                handleUploadProductTestDocument={handleUploadProductTestDocument}
                handleUploadProviderBillDocument={handleUploadProviderBillDocument}
                isLoadingApplications={isLoadingApplications}
                isLoadingLaboReportGrid={isLoadingLaboReportGrid}
                isLoadingProviderBill={isLoadingProviderBill}
                isLoadingSave={isLoadingSave}
                isLoadingTestDetails={isLoadingTestDetails}
                productTestDocuments={testDetails?.ptldDocumentsProjection}
              />,
              <ProductTestContactsPage
                changedFields={changedFields}
                isLoadingTestDetails={isLoadingTestDetails}
                productTestContacts={testDetails?.ptldContactsProjection}
              />,
              <Grid/>,
              <Grid/>
            ][tabSelected]}
          </Suspense>
          {displayControls &&
            (<Grid className={classes.controlsContainer} container direction='column'>
              <div>
                <Fab aria-label="add" className={classes.noteContainer} color="primary" onClick={handleNoteOpen} >
                  <Badge badgeContent={unreadNoteNumber} color="error" overlap="rectangular">
                    <CustomIcon className={classes.noteIcon} icon={faMessage} />
                  </Badge>
                  {isLoadingNote  ?
                    <CircularProgress className={classes.loadingNote} size={15}/>
                    :
                    <div className={classes.noteNumber}>
                      {noteNumber}
                    </div>
                  }
                </Fab>
              </div>
              {!isLoadingSave ?
                <div>
                  <Fab aria-label="add" className={classes.saveContainer} color="primary" onClick={handleSave} >
                    <CustomIcon className={classes.saveIcon} icon={faSave} />
                  </Fab>
                </div>
                :
                <CircularProgress className={classes.loadingSave} size={50}/>
              }
            </Grid>)
          }
        </PageContent>
      </BasicPageContainer>
    </>
  );
};